import { apiClient } from '../../apiHelper';
import { useFetch, useCreate, useGenericMutation } from '../react-query';

interface DeviceData {
  deviceName: string;
  hostname: string;
  port: number;
  description?: string;
  manufacturer?: string;
  model?: string;
  stationName?: string;
  institutionName?: string;
  institutionAddress?: string;
  institutionalDepartmentName?: string;
  connectionName?: string;
  connectionDescription?: string;
  maxOpsInvoked?: number;
  maxOpsPerformed?: number;
  connectTimeout?: number;
  requestTimeout?: number;
  acceptTimeout?: number;
  releaseTimeout?: number;
  responseTimeout?: number;
  tlsVersion?: string;
  cipherSuites?: string[];
  clientAuthRequired?: boolean;
  [key: string]: unknown;
}

interface PemDeviceData extends DeviceData {
  certificatePem: string;
  privateKeyPem: string;
  caCertificatePem?: string;
  clientAuthRequired?: boolean;
}

export function useGetDevices() {
  const getDevicesFn = async () => {
    const response = await apiClient.get('/pacs/devices');
    if (response?.data?.devices) {
      return response.data.devices;
    }
    return response || [];
  };

  return useFetch(getDevicesFn, ['pacs', 'devices']);
}

export function useGetDevicesWithConnections() {
  const getDevicesWithConnectionsFn = async () => {
    const response = await apiClient.get('/pacs/devices/with-connections');
    if (response?.data?.devices) {
      return response.data.devices;
    }
    return response || [];
  };

  return useFetch(getDevicesWithConnectionsFn, [
    'pacs',
    'devices-with-connections',
  ]);
}

export function useCreateDeviceWithTls() {
  const createDeviceFn = async (deviceData: DeviceData) => {
    const response = await apiClient.post(
      '/pacs/devices/create-with-tls',
      deviceData
    );
    // Extract device data from BaseResponse format
    if (response?.data) {
      return response.data;
    }
    // Fallback for legacy format
    return response;
  };

  return useCreate(
    createDeviceFn,
    ['pacs', 'devices'],
    'Error creating device with auto-TLS',
    'Device created successfully with auto-TLS'
  );
}

export function useCreateDeviceWithPemTls() {
  const createDeviceFn = async (deviceData: PemDeviceData) => {
    const response = await apiClient.post(
      '/pacs/devices/create-with-pem-tls',
      deviceData
    );
    // Extract device data from BaseResponse format
    if (response?.data) {
      return response.data;
    }
    // Fallback for legacy format
    return response;
  };

  return useCreate(
    createDeviceFn,
    ['pacs', 'devices'],
    'Error creating device with PEM-TLS',
    'Device created successfully with PEM-TLS'
  );
}

export function useCreateDeviceWithPfxTls() {
  const createDeviceFn = async (deviceData: Record<string, unknown>) => {
    const response = await apiClient.post(
      '/pacs/devices/create-with-pfx-tls',
      deviceData
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useCreate(
    createDeviceFn,
    ['pacs', 'devices'],
    'Error creating device from the PFX bundle',
    'Device created successfully from the PFX bundle'
  );
}

export function useStartDevice() {
  const startDeviceFn = async (deviceId: number) => {
    const response = await apiClient.post(`/pacs/devices/${deviceId}/start`);
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    startDeviceFn,
    ['pacs', 'devices'],
    'Error starting device',
    'Device started successfully'
  );
}

export function useStopDevice() {
  const stopDeviceFn = async (deviceId: number) => {
    const response = await apiClient.post(`/pacs/devices/${deviceId}/stop`);
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    stopDeviceFn,
    ['pacs', 'devices'],
    'Error stopping device',
    'Device stopped successfully'
  );
}

export function useRestartDevice() {
  const restartDeviceFn = async (deviceId: number) => {
    const response = await apiClient.post(`/pacs/devices/${deviceId}/restart`);
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    restartDeviceFn,
    ['pacs', 'devices'],
    'Error restarting device',
    'Device restarted successfully'
  );
}

export function useUpdateDevice() {
  const updateDeviceFn = async ({
    deviceId,
    deviceData,
  }: {
    deviceId: number;
    deviceData: {
      deviceName?: string;
      description?: string;
      manufacturer?: string;
      manufacturerModelName?: string;
      stationName?: string;
      institutionName?: string;
      institutionalDepartmentName?: string;
    };
  }) => {
    const response = await apiClient.put(
      `/pacs/devices/${deviceId}`,
      deviceData
    );
    if (response?.data) {
      return response;
    }
    return response;
  };

  return useGenericMutation(
    updateDeviceFn,
    ['pacs', 'devices'],
    'Error updating device',
    'Device updated successfully'
  );
}

export async function downloadCertificateBundle(deviceId: number) {
  try {
    // Use the new, specialized method from our apiClient
    const response = await apiClient.downloadFile(
      `/pacs/devices/${deviceId}/certificates/bundle`
    );

    // The full response object is returned, so we can access everything we need
    const blob = response.data; // The blob is in response.data
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/zip')) {
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `dicom-mobile-certs-device-${deviceId}.zip`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      throw new Error('Unexpected response format from server');
    }
  } catch (error) {
    const axiosError = error as any;
    if (axiosError.response) {
      // The error response body will also be a Blob because of the request config.
      // We need to read it as text to parse the JSON error message.
      const errorBlob = axiosError.response.data as Blob;
      const contentType = axiosError.response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const errorText = await errorBlob.text();
        const errorData = JSON.parse(errorText);
        const errorMessage =
          errorData.message || 'Failed to download certificate bundle';
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = `Failed to download certificate bundle: ${axiosError.response.statusText}`;
        console.error('HTTP Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } else {
      // Network error or request setup error
      console.error('Network Error:', error);
      throw error;
    }
  }
}

export async function downloadClientKey(deviceId: number) {
  try {
    const response = await apiClient.downloadFile(
      `/pacs/devices/${deviceId}/certificates/client-key`
    );

    const blob = response.data;
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/x-pem-file')) {
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `dicom-client-key-device-${deviceId}.pem`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      throw new Error('Unexpected response format from server');
    }
  } catch (error) {
    const axiosError = error as any;
    if (axiosError.response) {
      const errorBlob = axiosError.response.data as Blob;
      const contentType = axiosError.response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const errorText = await errorBlob.text();
        const errorData = JSON.parse(errorText);
        const errorMessage =
          errorData.message || 'Failed to download client key';
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = `Failed to download client key: ${axiosError.response.statusText}`;
        console.error('HTTP Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } else {
      console.error('Network Error:', error);
      throw error;
    }
  }
}

export async function downloadClientCertificate(deviceId: number) {
  try {
    const response = await apiClient.downloadFile(
      `/pacs/devices/${deviceId}/certificates/client-certificate`
    );

    const blob = response.data;
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/x-pem-file')) {
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `dicom-client-cert-device-${deviceId}.pem`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      throw new Error('Unexpected response format from server');
    }
  } catch (error) {
    const axiosError = error as any;
    if (axiosError.response) {
      const errorBlob = axiosError.response.data as Blob;
      const contentType = axiosError.response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const errorText = await errorBlob.text();
        const errorData = JSON.parse(errorText);
        const errorMessage =
          errorData.message || 'Failed to download client certificate';
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = `Failed to download client certificate: ${axiosError.response.statusText}`;
        console.error('HTTP Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } else {
      console.error('Network Error:', error);
      throw error;
    }
  }
}

export async function downloadCACertificate(deviceId: number) {
  try {
    const response = await apiClient.downloadFile(
      `/pacs/devices/${deviceId}/certificates/ca-certificate`
    );

    const blob = response.data;
    const contentType = response.headers['content-type'];

    if (contentType && contentType.includes('application/x-pem-file')) {
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `dicom-ca-cert-device-${deviceId}.pem`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      throw new Error('Unexpected response format from server');
    }
  } catch (error) {
    const axiosError = error as any;
    if (axiosError.response) {
      const errorBlob = axiosError.response.data as Blob;
      const contentType = axiosError.response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const errorText = await errorBlob.text();
        const errorData = JSON.parse(errorText);
        const errorMessage =
          errorData.message || 'Failed to download CA certificate';
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      } else {
        const errorMessage = `Failed to download CA certificate: ${axiosError.response.statusText}`;
        console.error('HTTP Error:', errorMessage);
        throw new Error(errorMessage);
      }
    } else {
      console.error('Network Error:', error);
      throw error;
    }
  }
}

export function useDeleteDevice() {
  const deleteDeviceFn = async (deviceId: number) => {
    const response = await apiClient.delete(`/pacs/devices/${deviceId}`);
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    deleteDeviceFn,
    ['pacs', 'devices'],
    'Error deleting device',
    'Device deleted successfully'
  );
}

export function useDeleteConnection() {
  const deleteConnectionFn = async ({
    deviceId,
    connectionId,
  }: {
    deviceId: number;
    connectionId: number;
  }) => {
    const response = await apiClient.delete(
      `/pacs/devices/${deviceId}/connections/${connectionId}`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    deleteConnectionFn,
    ['pacs', 'devices'],
    'Error deleting connection',
    'Connection deleted successfully'
  );
}

export function useDeleteApplicationEntity() {
  const deleteApplicationEntityFn = async ({
    deviceId,
    aeId,
  }: {
    deviceId: number;
    aeId: number;
  }) => {
    const response = await apiClient.delete(
      `/pacs/devices/${deviceId}/aes/${aeId}`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    deleteApplicationEntityFn,
    ['pacs', 'devices'],
    'Error deleting application entity',
    'Application entity deleted successfully'
  );
}

export function useStartApplicationEntity() {
  const startApplicationEntityFn = async ({
    deviceId,
    aeId,
  }: {
    deviceId: number;
    aeId: number;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/aes/${aeId}/start`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    startApplicationEntityFn,
    ['pacs', 'devices'],
    'Error starting application entity',
    'Application entity started successfully'
  );
}

export function useStopApplicationEntity() {
  const stopApplicationEntityFn = async ({
    deviceId,
    aeId,
  }: {
    deviceId: number;
    aeId: number;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/aes/${aeId}/stop`
    );
    // Extract data from BaseResponse format
    if (response?.data) {
      return response.data;
    }
    // Fallback for legacy format
    return response;
  };

  return useGenericMutation(
    stopApplicationEntityFn,
    ['pacs', 'devices'],
    'Error stopping application entity',
    'Application entity stopped successfully'
  );
}

export function useStopAllConnectionAes() {
  const stopAllConnectionAesFn = async ({
    deviceId,
    connectionId,
  }: {
    deviceId: number;
    connectionId: number;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/stop-all`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    stopAllConnectionAesFn,
    ['pacs', 'devices'],
    'Error stopping all application entities for connection',
    'All application entities stopped successfully'
  );
}

export function useStartAllConnectionAes() {
  const startAllConnectionAesFn = async ({
    deviceId,
    connectionId,
  }: {
    deviceId: number;
    connectionId: number;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/start-all`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    startAllConnectionAesFn,
    ['pacs', 'devices'],
    'Error starting all application entities for connection',
    'All application entities started successfully'
  );
}

export function useRestartAllConnectionAes() {
  const restartAllConnectionAesFn = async ({
    deviceId,
    connectionId,
  }: {
    deviceId: number;
    connectionId: number;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/restart-all`
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    restartAllConnectionAesFn,
    ['pacs', 'devices'],
    'Error restarting all application entities for connection',
    'All application entities restarted successfully'
  );
}

export function useCheckPortAvailability(port: number) {
  const checkPortFn = async () => {
    const response = await apiClient.get(
      `/pacs/connections/check-port/${port}`
    );
    return response;
  };

  return useFetch(checkPortFn, ['pacs', 'port-availability', String(port)]);
}

export function useGetUsedPorts() {
  const getUsedPortsFn = async () => {
    const response = await apiClient.get('/pacs/connections/used-ports');
    return response;
  };

  return useFetch(getUsedPortsFn, ['pacs', 'used-ports']);
}

export function useCreateConnection() {
  const createConnectionFn = async ({
    deviceId,
    connectionData,
  }: {
    deviceId: number;
    connectionData: {
      connectionName: string;
      hostname: string;
      port: number;
      description?: string;
      deviceId?: number;
      connectionRole?: 'SCP_LISTEN' | 'SCU_REMOTE';
      tlsEnabled?: boolean;
    };
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections`,
      connectionData
    );
    return response;
  };

  return useGenericMutation(
    createConnectionFn,
    ['pacs', 'connections'],
    'Error creating connection',
    'Connection created successfully'
  );
}

export function useCreateApplicationEntity() {
  const createApplicationEntityFn = async ({
    deviceId,
    connectionId,
    aeData,
  }: {
    deviceId: number;
    connectionId: number;
    aeData: {
      aeTitle: string;
      description?: string;
      vendorData?: string;
      applicationClusters?: string;
      preferredCallingAeTitles?: string;
      preferredCalledAeTitles?: string;
      acceptedCalledAeTitles?: string;
      otherAeTitles?: string;
      masqueradingAeTitles?: string;
      aeRole: 'SCP' | 'SCU' | 'BOTH';
      // aeType: 'BLIND' | 'OB';
      installed?: boolean;
      associator?: boolean;
      associateAcceptor?: boolean;
      status?: 'ACTIVE' | 'INACTIVE';
      deviceId?: number;
      connectionId?: number;
      createdBy?: string;
      transferCapabilities?: unknown[];
      services?: unknown[];
    };
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes`,
      aeData
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    createApplicationEntityFn,
    ['pacs', 'devices'],
    'Error creating application entity',
    'Application entity created successfully'
  );
}

export function useSearchUsersWithAcceptanceStatus() {
  const searchUsersFn = async ({
    deviceId,
    connectionId,
    aeId,
    query,
  }: {
    deviceId: number;
    connectionId: number;
    aeId: number;
    query: string;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/${aeId}/search-users-with-status`,
      null,
      { params: { query } }
    );
    return response;
  };

  return useGenericMutation(
    searchUsersFn,
    ['pacs', 'users-acceptance-status'],
    'Error searching users',
    'Users searched successfully'
  );
}

export function useAddAcceptedCallingAeTitle() {
  const addAcceptedCallingAeTitleFn = async ({
    deviceId,
    connectionId,
    aeId,
    userId,
  }: {
    deviceId: number;
    connectionId: number;
    aeId: number;
    userId: string;
  }) => {
    const response = await apiClient.post(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/${aeId}/accepted-calling-ae-titles`,
      { user_id: userId }
    );
    return response;
  };

  return useGenericMutation(
    addAcceptedCallingAeTitleFn,
    ['pacs', 'devices'],
    'Error adding user',
    'User added successfully'
  );
}

export function useRemoveAcceptedCallingAeTitle() {
  const removeAcceptedCallingAeTitleFn = async ({
    deviceId,
    connectionId,
    aeId,
    acceptedTitleId,
  }: {
    deviceId: number;
    connectionId: number;
    aeId: number;
    acceptedTitleId: number;
  }) => {
    const response = await apiClient.delete(
      `/pacs/devices/${deviceId}/connections/${connectionId}/aes/${aeId}/accepted-calling-ae-titles/${acceptedTitleId}`
    );
    return response;
  };

  return useGenericMutation(
    removeAcceptedCallingAeTitleFn,
    ['pacs', 'devices'],
    'Error removing user',
    'User removed successfully'
  );
}

export function useGetPacsStudiesPageable() {
  const getStudiesPageableFn = async ({
    aeType,
    synced,
    page = 0,
    size = 20,
    sortBy = 'receivedAt',
    sortDirection = 'DESC',
  }: {
    aeType?: string;
    synced?: boolean;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
  }) => {
    const params = new URLSearchParams();

    if (aeType) {
      params.append('ae_type', aeType);
    }
    if (synced !== undefined) {
      params.append('synced', String(synced));
    }
    params.append('page', String(page));
    params.append('size', String(size));
    params.append('sort_by', sortBy);
    params.append('sort_direction', sortDirection);

    const response = await apiClient.get(
      `/pacs/studies/pageable?${params.toString()}`
    );
    return response;
  };

  return useGenericMutation(
    getStudiesPageableFn,
    ['pacs', 'studies-pageable'],
    'pacs.studiesFetchFailed',
    'pacs.studiesFetchedSuccess'
  );
}

export function useGetPacsStudyWithInstances() {
  const getStudyWithInstancesFn = async (studyId: number) => {
    const response = await apiClient.get(
      `/pacs/studies/${studyId}/with-instances`
    );
    return response;
  };

  return useGenericMutation(
    getStudyWithInstancesFn,
    ['pacs', 'study-with-instances'],
    'pacs.studyDetailsFetchFailed',
    'pacs.studyDetailsFetchedSuccess'
  );
}

export function useSyncPacsStudy() {
  const syncStudyFn = async (studyInstanceUid: string) => {
    const response = await apiClient.post(
      `/pacs/sync/study/${studyInstanceUid}`
    );
    return response;
  };

  return useGenericMutation(
    syncStudyFn,
    ['pacs', 'sync-study'],
    'Error syncing study',
    'Study synced successfully'
  );
}

export function useComprehensivePacsSync() {
  const comprehensiveSyncFn = async ({
    studyInstanceUid,
    syncData,
  }: {
    studyInstanceUid?: string;
    syncData?: {
      patient_id?: string;
      patient_data?: {
        patient_name?: string;
        patient_last_name?: string;
        email?: string;
        birth_date?: string;
        phone_number?: string;
        [key: string]: unknown;
      };
      pregnancy_id?: number;
      pregnancy_data?: {
        last_menstrual_period?: string;
        pregnancy_type?: string;
        dating_method?: string;
        [key: string]: unknown;
      };
      study_category: 'T1' | 'T2' | 'T3';
      type?: 'OB' | 'BLIND' | string;
      serie_instance_uid?: string;
      pacs_study_data?: {
        studyDate?: string;
        aeType?: string;
        last_menstrual_period?: string;
        [key: string]: unknown;
      };
    };
  }) => {
    const response = await apiClient.post(
      `/pacs/sync/study/${studyInstanceUid}/comprehensive`,
      syncData
    );
    return response;
  };

  return useGenericMutation(
    comprehensiveSyncFn,
    ['pacs', 'studies'],
    'Error during comprehensive sync',
    'Study synced successfully with patient and pregnancy data'
  );
}

export function useBlindComprehensivePacsSync() {
  const comprehensiveSyncFn = async ({
    studyInstanceUid,
    syncData,
  }: {
    studyInstanceUid?: string;
    syncData?: {
      patient_id?: string;
      patient_data?: {
        patient_name?: string;
        patient_last_name?: string;
        email?: string;
        birth_date?: string;
        phone_number?: string;
        [key: string]: unknown;
      };
      pregnancy_id?: number;
      pregnancy_data?: {
        last_menstrual_period?: string;
        pregnancy_type?: string;
        dating_method?: string;
        [key: string]: unknown;
      };
      study_category: 'T1' | 'T2' | 'T3';
      type?: 'OB' | 'BLIND' | string;
      serie_instance_uid?: string;
      pacs_study_data?: {
        studyDate?: string;
        aeType?: string;
        last_menstrual_period?: string;
        [key: string]: unknown;
      };
    };
  }) => {
    const response = await apiClient.post(
      `/pacs/sync/blind_study/${studyInstanceUid}/comprehensive`,
      syncData
    );
    return response;
  };

  return useGenericMutation(
    comprehensiveSyncFn,
    ['pacs', 'studies'],
    'Error during comprehensive sync',
    'Study synced successfully with patient and pregnancy data'
  );
}

export interface ComprehensiveAEUpdateData {
  aeTitle: string;
  name?: string;
  description?: string;
  aeRole?: 'SCP' | 'SCU' | 'BOTH';
  aeType?: 'BLIND' | 'OB';
  acceptedCalledAeTitles?: string;
  installed?: boolean;
  associator?: boolean;
  associateAcceptor?: boolean;
  status?: 'ACTIVE' | 'INACTIVE';
  // Always required - contains ALL SOP classes, never empty array
  transferCapabilities: Array<{
    id?: number;
    sopClass: string;
    role: 'SCP' | 'SCU';
    transferSyntaxes: string; // Comma-separated UIDs or empty string "" to delete this SOP class
    commonName?: string;
    enabled?: boolean;
    priority?: number;
  }>;
  services?: Array<{
    id?: number;
    serviceType: 'C_ECHO' | 'C_STORE' | 'C_FIND' | 'C_MOVE' | 'C_GET';
    description?: string;
    enabled?: boolean;
    priority?: number;
    configuration?: string;
    sopClasses: Array<{
      id?: number;
      sopClassUid: string;
      sopClassName?: string;
    }>;
  }>;
  acceptedCallingAeTitles?: Array<{
    id?: number;
    callingAeTitle: string;
    doctorId?: string;
    doctorFirstName?: string;
    doctorLastName?: string;
    doctorRole?: string;
    organization?: string;
    description?: string;
    enabled?: boolean;
  }>;
}

export function useUpdateComprehensiveApplicationEntity() {
  const updateAEFn = async ({
    aeId,
    aeData,
  }: {
    aeId: number;
    aeData: ComprehensiveAEUpdateData;
  }) => {
    const response = await apiClient.put(
      `/pacs/aes/${aeId}/comprehensive`,
      aeData
    );
    if (response?.data) {
      return response.data;
    }
    return response;
  };

  return useGenericMutation(
    updateAEFn,
    ['pacs', 'application-entities'],
    'Error updating application entity',
    'Application entity updated successfully'
  );
}

export function useGetApplicationEntitiesPageable({
  page = 0,
  size = 10,
  sort = 'id',
  direction = 'ASC',
  deviceId,
}: {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'ASC' | 'DESC';
  deviceId?: number | null;
} = {}) {
  const getAEsPageableFn = async () => {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort,
      direction,
    });

    if (deviceId !== null && deviceId !== undefined) {
      params.append('device_id', String(deviceId));
    }

    const response = await apiClient.get(`/pacs/aes?${params.toString()}`);

    return response?.data;
  };

  return useFetch(getAEsPageableFn, [
    'pacs',
    'aes-pageable',
    String(page),
    String(size),
    sort,
    direction,
    deviceId === null ? 'all' : String(deviceId || ''),
  ]);
}
